import React from "react"
import ReactDOM from "react-dom/client"
import Sensor, { displayNameForSensor } from "../../models/sensor"
import Project from "../../models/project"
import Placement from "../../models/placement"
import moment from "moment"
import domtoimage from "dom-to-image"
import { createAndDownloadPDF } from "./TemperatureDifferential/TemperatureDifferentialReportPreviewView"
import { logExport } from "../../utilities/analytics"
import { Box, Typography } from "@mui/material"
import TemperatureDifferenceChart from "../charts/TemperatureDifferenceChart"
import TemperatureChart from "../charts/TemperatureChart"
import ProjectSummaryTableView from "./ProjectSummaryTableView"
import DifferentialSummaryTableView from "./TemperatureDifferential/DifferentialSummaryTableView"
import SelectedSensorsSummaryTableView from "./SelectedSensorsSummaryTableView"
import MaximumAndMinimumSensorsTableView from "./TemperatureDifferential/MaximumAndMinimumSensorsTableView"
import SensorSummaryTableView from "./SelectedSensors/SensorSummaryTableView"
import SensorInfoTableView from "./SelectedSensors/SensorInfoTableView"
import MaturityInfoTableView from "./SelectedSensors/MaturityInfoTableView"
import StrengthChart from "../charts/StrengthChart"
import MaturityChart from "../charts/MaturityChart"

const pdfMargins = 8

const waitForChartsToRender = (ms = 2000): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export const generateTemperatureDifferentialReport = async (
  project: Project,
  placement: Placement,
  selectedSensors: Sensor[],
  colorMap: Map<number, string>,
  startDate: moment.Moment,
  endDate: moment.Moment
) => {
  // Create a hidden container
  const container = document.createElement("div")
  container.style.position = "absolute"
  container.style.left = "-9999px"
  container.style.top = "-9999px"
  container.style.width = "8.5in"
  container.style.backgroundColor = "#ffffff"
  container.style.padding = "12px"
  document.body.appendChild(container)

  const root = ReactDOM.createRoot(container)

  try {
    // Render the report content
    root.render(
      <Box display="flex" flexDirection="column" gap="18px">
        <ProjectSummaryTableView
          id="project-summary-table"
          projectName={project.name}
          placement={placement}
          dateRangeStart={startDate}
          dateRangeEnd={endDate}
        />
        <DifferentialSummaryTableView
          id="differential-summary-table"
          selectedSensors={selectedSensors}
          temperatureUnit={placement.temperatureUnit}
          timezone={placement.timeZone}
          startDate={startDate}
          endDate={endDate}
        />
        <Box id="temperature-report-chart" sx={{
          width: "100%",
          height: 300,
          backgroundColor: "#ffffff"
        }}>
          <TemperatureChart
            forReport={true}
            temperatureUnit={placement.temperatureUnit}
            sensors={selectedSensors}
            colorMap={colorMap}
            timezone={placement.timeZone}
            min={placement.temperatureLimit?.minimumTemperature ?? undefined}
            max={placement.temperatureLimit?.maximumTemperature ?? undefined}
            startDate={startDate}
            endDate={endDate} projectId={0} placementId={0} />
        </Box>
        <Box id="temperature-difference-report-chart" sx={{
          width: "100%",
          height: 300,
          backgroundColor: "#ffffff"
        }}>
          <TemperatureDifferenceChart
            forReport={true}
            temperatureUnit={placement.temperatureUnit}
            sensors={selectedSensors}
            colorMap={colorMap}
            placement={placement}
            threshold={placement.temperatureLimit?.difference ?? undefined}
            start={startDate}
            end={endDate}
          />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Typography variant="body1" sx={{ marginBottom: "12px" }}>
            Selected Sensors
          </Typography>
          <SelectedSensorsSummaryTableView id="selected-sensors-summary-table" sensors={selectedSensors} />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Typography variant="body1" sx={{ marginBottom: "12px" }}>
            Maximum & Minimum Sensor Data
          </Typography>
          <MaximumAndMinimumSensorsTableView
            id="maximum-and-minimum-sensors-summary-table"
            sensors={selectedSensors}
            temperatureUnit={placement.temperatureUnit}
            timezone={placement.timeZone}
            startDate={startDate}
            endDate={endDate}
          />
        </Box>
        <Box id="signature-line" display="flex" flexDirection="row" gap="10px" sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: "1px solid black", width: "50%", padding: "12px 0" }}>
            <Typography variant="caption" color="black">
              Signature
            </Typography>
          </Box>
          <Box sx={{ borderBottom: "1px solid black", width: "50%", padding: "12px 0" }}>
            <Typography variant="caption" color="black">
              Print
            </Typography>
          </Box>
        </Box>
      </Box>
    )

    // Wait for charts to render
    await waitForChartsToRender()

    // Capture DOM elements
    const projectSummary = document.getElementById("project-summary-table")
    if (!projectSummary) {
      throw new Error("Project summary table not found")
    }
    const projectSummaryTable = await domtoimage.toJpeg(projectSummary)

    const differentialSummary = document.getElementById("differential-summary-table")
    if (!differentialSummary) {
      throw new Error("Differential summary table not found")
    }
    const differentialSummaryTable = await domtoimage.toJpeg(differentialSummary)

    const temperatureChart = document.getElementById("temperature-report-chart")
    if (!temperatureChart) {
      throw new Error("Temperature chart not found")
    }
    let temperatureChartImage = await domtoimage.toPng(temperatureChart)
    temperatureChartImage = await domtoimage.toPng(temperatureChart)

    const temperatureDifferenceChart = document.getElementById("temperature-difference-report-chart")
    if (!temperatureDifferenceChart) {
      throw new Error("Temperature difference chart not found")
    }
    let temperatureDifferenceChartImage = await domtoimage.toPng(temperatureDifferenceChart)
    temperatureDifferenceChartImage = await domtoimage.toPng(temperatureDifferenceChart)

    const selectedSensorsSummary = document.getElementById("selected-sensors-summary-table")
    if (!selectedSensorsSummary) {
      throw new Error("Selected sensors summary table not found")
    }
    const selectedSensorsSummaryTable = await domtoimage.toJpeg(selectedSensorsSummary)

    const maximumAndMinimumSensorsSummary = document.getElementById("maximum-and-minimum-sensors-summary-table")
    if (!maximumAndMinimumSensorsSummary) {
      throw new Error("Maximum and minimum sensors summary table not found")
    }
    const maximumAndMinimumSensorsSummaryTable = await domtoimage.toJpeg(maximumAndMinimumSensorsSummary)

    const signatureLine = document.getElementById("signature-line")
    if (!signatureLine) {
      throw new Error("Signature line not found")
    }
    const signatureLineImage = await domtoimage.toPng(signatureLine)

    const content = [
      {
        image: projectSummaryTable,
        width: 400,
        margin: pdfMargins
      },
      {
        image: differentialSummaryTable,
        width: 400,
        margin: pdfMargins
      },
      {
        image: temperatureChartImage,
        width: 500,
        height: 230,
        margin: pdfMargins
      },
      {
        image: temperatureDifferenceChartImage,
        width: 500,
        height: 230,
        margin: pdfMargins
      },
      {
        text: "Selected Sensors",
        margin: pdfMargins,
        fontSize: 10,
      },
      {
        image: selectedSensorsSummaryTable,
        width: 500,
        margin: pdfMargins
      },
      {
        text: "Maximum & Minimum Sensor Data",
        fontSize: 10,
        margin: pdfMargins
      },
      {
        image: maximumAndMinimumSensorsSummaryTable,
        width: 500,
        margin: pdfMargins
      },
      {
        image: signatureLineImage,
        width: 500,
        margin: pdfMargins
      }
    ]

    const title = `${project.name} > ${placement.name}`
    const filename = placement.name + "_" + moment().format("YYYYMMDD[T]HHmmss") + ".pdf"
    createAndDownloadPDF(content, filename, title)

    logExport("temperature_differential", selectedSensors.length)
  } finally {
    // Clean up
    root.unmount()
    document.body.removeChild(container)
  }
}

export const generateSelectedSensorsReport = async (
  project: Project,
  placement: Placement,
  selectedSensors: Sensor[],
  colorMap: Map<number, string>,
  startDate: moment.Moment,
  endDate: moment.Moment
) => {
  // Create a hidden container
  const container = document.createElement("div")
  container.style.position = "absolute"
  container.style.left = "-9999px"
  container.style.top = "-9999px"
  container.style.width = "8.5in"
  container.style.backgroundColor = "#ffffff"
  container.style.padding = "12px"
  document.body.appendChild(container)

  const root = ReactDOM.createRoot(container)

  try {
    const hasStrength = placement.maturityMethod.curveFit.type !== "disabled"
    const hasMaturity = placement.maturityMethod.type !== "disabled"

    // Render the report content
    root.render(
      <Box display="flex" flexDirection="column" gap="18px">
        {selectedSensors.map(sensor => (
          <React.Fragment key={sensor.id}>
            <Typography id={`sensor-name-${sensor.id}`} variant="h6" sx={{ marginBottom: "12px" }}>
              {displayNameForSensor(sensor)}
            </Typography>
            <SensorSummaryTableView
              id={`sensor-summary-table-${sensor.id}`}
              sensor={sensor}
              placement={placement}
              startDate={startDate}
              endDate={endDate}
            />
            <SensorInfoTableView
              id={`sensor-info-table-${sensor.id}`}
              sensor={sensor}
              placement={placement}
            />
            <MaturityInfoTableView
              id={`maturity-info-table-${sensor.id}`}
              maturityMethod={placement.maturityMethod}
            />
            {hasStrength &&
              <Box id={`strength-chart-${sensor.id}`} sx={{
                width: "100%",
                height: 575,
                backgroundColor: "#ffffff"
              }}>
                <StrengthChart
                  sensors={[sensor]}
                  placement={placement}
                  colorMap={colorMap}
                  startDate={startDate}
                  endDate={endDate}
                />
              </Box>
            }
            {hasMaturity &&
              <Box id={`maturity-chart-${sensor.id}`} sx={{
                width: "100%",
                height: 575,
                backgroundColor: "#ffffff"
              }}>
                <MaturityChart
                  sensors={[sensor]}
                  placement={placement}
                  colorMap={colorMap}
                  startDate={startDate}
                  endDate={endDate}
                />
              </Box>
            }
            <Box id={`temperature-chart-${sensor.id}`} sx={{
              width: "100%",
              height: 575,
              backgroundColor: "#ffffff"
            }}>
              <TemperatureChart
                forReport={true}
                temperatureUnit={placement.temperatureUnit}
                sensors={[sensor]}
                colorMap={colorMap}
                timezone={placement.timeZone}
                min={placement.temperatureLimit?.minimumTemperature ?? undefined}
                max={placement.temperatureLimit?.maximumTemperature ?? undefined}
                startDate={startDate}
                endDate={endDate}
                projectId={0}
                placementId={0}
              />
            </Box>
          </React.Fragment>
        ))}
        {selectedSensors.length > 1 &&
          <Box id="combined-temperature-chart" sx={{
            width: "100%",
            minHeight: 1000,
            backgroundColor: "#ffffff"
          }}>
            <TemperatureChart
              forReport={true}
              temperatureUnit={placement.temperatureUnit}
              sensors={selectedSensors}
              colorMap={colorMap}
              timezone={placement.timeZone}
              min={placement.temperatureLimit?.minimumTemperature ?? undefined}
              max={placement.temperatureLimit?.maximumTemperature ?? undefined}
              startDate={startDate}
              endDate={endDate}
              title={`Combined Temperature (${placement.temperatureUnit === "c" ? "°C" : "°F"})`} projectId={0} placementId={0} />
          </Box>
        }
        {hasMaturity && selectedSensors.length > 1 &&
          <Box id="combined-maturity-chart" sx={{
            width: "100%",
            minHeight: 1000,
            backgroundColor: "#ffffff"
          }}>
            <MaturityChart
              sensors={selectedSensors}
              placement={placement}
              colorMap={colorMap}
              startDate={startDate}
              endDate={endDate}
              title={`Combined TTF (${placement.maturityMethod.temperatureUnit === "c" ? "°C" : "°F"})`}
            />
          </Box>
        }
        {hasStrength && selectedSensors.length > 1 &&
          <Box id="combined-strength-chart" sx={{
            width: "100%",
            minHeight: 1000,
            backgroundColor: "#ffffff"
          }}>
            <StrengthChart
              sensors={selectedSensors}
              placement={placement}
              colorMap={colorMap}
              startDate={startDate}
              endDate={endDate}
              title={`Combined Strength (${placement.maturityMethod.curveFit.parameters?.strengthUnit})`}
            />
          </Box>
        }
      </Box>
    )

    // Wait for charts to render
    await waitForChartsToRender()

    // Capture DOM elements and build content
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const content: any = await Promise.all(selectedSensors.flatMap(async (sensor) => {
      const sensorSummary = document.getElementById(`sensor-summary-table-${sensor.id}`)
      const sensorSummaryTable = await domtoimage.toJpeg(sensorSummary as HTMLElement)

      const sensorInfo = document.getElementById(`sensor-info-table-${sensor.id}`)
      const sensorInfoTable = await domtoimage.toJpeg(sensorInfo as HTMLElement)

      const sensorContent: unknown[] = [
        {
          text: displayNameForSensor(sensor),
          margin: pdfMargins,
          fontSize: 16,
          bold: true
        },
        {
          image: sensorSummaryTable,
          width: 500,
          margin: pdfMargins,
        },
        {
          image: sensorInfoTable,
          width: 300,
          margin: pdfMargins
        }
      ]

      if (hasMaturity) {
        const maturityInfo = document.getElementById(`maturity-info-table-${sensor.id}`)
        const maturityInfoTable = await domtoimage.toJpeg(maturityInfo as HTMLElement)
        sensorContent.push({
          image: maturityInfoTable,
          width: 300,
          margin: pdfMargins
        })
      }

      if (hasStrength) {
        const strengthChart = document.getElementById(`strength-chart-${sensor.id}`)
        const strengthChartImage = await domtoimage.toPng(strengthChart as HTMLElement)
        sensorContent.push({
          image: strengthChartImage,
          width: 500,
          margin: pdfMargins
        })
      }

      if (hasMaturity) {
        const maturityChart = document.getElementById(`maturity-chart-${sensor.id}`)
        const maturityChartImage = await domtoimage.toPng(maturityChart as HTMLElement)
        sensorContent.push({
          image: maturityChartImage,
          width: 500,
          margin: pdfMargins
        })
      }

      const temperatureChart = document.getElementById(`temperature-chart-${sensor.id}`)
      const temperatureChartImage = await domtoimage.toPng(temperatureChart as HTMLElement)
      sensorContent.push({
        image: temperatureChartImage,
        width: 500,
        margin: pdfMargins,
        pageBreak: "after"
      })

      return sensorContent
    }))

    if (selectedSensors.length > 1) {
      const combinedTemperatureChart = document.getElementById("combined-temperature-chart")
      const combinedTemperatureChartImage = await domtoimage.toPng(combinedTemperatureChart as HTMLElement)
      content.push({
        image: combinedTemperatureChartImage,
        width: 500,
        margin: pdfMargins
      })

      if (hasMaturity) {
        const combinedMaturityChart = document.getElementById("combined-maturity-chart")
        const combinedMaturityChartImage = await domtoimage.toPng(combinedMaturityChart as HTMLElement)
        content.push({
          image: combinedMaturityChartImage,
          width: 500,
          margin: pdfMargins
        })
      }

      if (hasStrength) {
        const combinedStrengthChart = document.getElementById("combined-strength-chart")
        const combinedStrengthChartImage = await domtoimage.toPng(combinedStrengthChart as HTMLElement)
        content.push({
          image: combinedStrengthChartImage,
          width: 500,
          margin: pdfMargins
        })
      }
    }

    const title = `${project.name} > ${placement.name}`
    const filename = placement.name + "_" + moment().format("YYYYMMDD[T]HHmmss") + ".pdf"
    createAndDownloadPDF(content, filename, title)
    logExport("selected_sensors", selectedSensors.length)
  } finally {
    // Clean up
    root.unmount()
    document.body.removeChild(container)
  }
}

